import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Select, 
  Slider, 
  Switch, 
  ColorPicker, 
  Tabs, 
  Card, 
  Space, 
  InputNumber, 
  Radio, 
  Divider,
  Typography,
  Button,
  Tooltip,
  Input
} from 'antd';
import { Settings, BarChart3, LineChart, PieChart, TrendingUp } from 'lucide-react';
import { getSmartColumnSuggestions, DEFAULT_CONFIGS } from '../../utils/visualizationHelpers';

const { TextArea } = Input;
const { Option } = Select;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

// Cryptique Design System styles
const styles = {
  primaryColor: "#1d0c46",
  accentColor: "#caa968",
  backgroundColor: "#f9fafb",
  cardBg: "white",
  textPrimary: "#111827",
  textSecondary: "#475569",
  borderColor: "rgba(255, 255, 255, 0.18)",
  glassEffect: "rgba(255, 255, 255, 0.7)",
  cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)"
};
const flatCardStyle = { borderRadius: 0, boxShadow: 'none' };

const VisualizationConfigModal = ({ 
  visible, 
  onClose, 
  visualizationType, 
  config, 
  onConfigChange, 
  availableColumns,
  previewData
}) => {
  const [form] = Form.useForm();
  const [localConfig, setLocalConfig] = useState({});

  // Initialize form with current config
  useEffect(() => {
    const hasConfig = config && typeof config === 'object' && Object.keys(config).length > 0;
    if (hasConfig) {
      // For KPI, ensure customLabels exist for all valueColumns
      if (visualizationType === 'KPI' && config.valueColumns && Array.isArray(config.valueColumns)) {
        const customLabels = config.customLabels || {};
        const updatedLabels = { ...customLabels };
        // Initialize empty labels for any valueColumns that don't have custom labels
        config.valueColumns.forEach(col => {
          if (!updatedLabels.hasOwnProperty(col)) {
            updatedLabels[col] = '';
          }
        });
        // Remove labels for columns that are no longer in valueColumns
        Object.keys(updatedLabels).forEach(col => {
          if (!config.valueColumns.includes(col)) {
            delete updatedLabels[col];
          }
        });
        const updatedConfig = { ...config, customLabels: updatedLabels };
        setLocalConfig(updatedConfig);
        form.setFieldsValue(updatedConfig);
      } else if (visualizationType === 'Pie Chart' && config.nameField && config.valueField) {
        // For Pie Chart, ensure sliceColors exist
        const sliceColors = config.sliceColors || {};
        setLocalConfig({ ...config, sliceColors });
        form.setFieldsValue({ ...config, sliceColors });
      } else {
        setLocalConfig(config);
        form.setFieldsValue(config);
      }
    } else if (visualizationType === 'Table' && availableColumns && availableColumns.length > 0) {
      // For Table, initialize with default column configuration if no config exists
      const defaultTableConfig = {
        tableName: '',
        columns: availableColumns.map(col => ({ key: col, label: col, color: undefined })),
        headerColor: '#ffffff',
        headerTextcolor: '#0f172a',
        zebraStripes: false,
        pageSize: 10
      };
      setLocalConfig(defaultTableConfig);
      form.setFieldsValue(defaultTableConfig);
    } else if (visualizationType === 'KPI' && availableColumns && availableColumns.length > 0) {
      // For KPI, initialize with first column as default
      const defaultKpiConfig = {
        valueColumns: [availableColumns[0]],
        customLabels: {},
        layout: 'horizontal',
        showTrend: false,
        colors: ['#1d0c46']
      };
      setLocalConfig(defaultKpiConfig);
      form.setFieldsValue(defaultKpiConfig);
    } else if (visualizationType === 'Pie Chart' && availableColumns && availableColumns.length >= 2) {
      // For Pie Chart, initialize with first two columns as name and value fields
      const defaultPieConfig = {
        nameField: availableColumns[0],
        valueField: availableColumns[1],
        innerRadius: 0,
        outerRadius: 80,
        showLabels: true,
        showPercentages: true,
        colors: ['#1d0c46', '#caa968', '#8b5cf6', '#10b981', '#f59e0b']
      };
      setLocalConfig(defaultPieConfig);
      form.setFieldsValue(defaultPieConfig);
    } else {
      // Non-table/KPI or no columns yet: load smart defaults so fresh queries open with defaults
      const defaults = getSmartDefaults();
      setLocalConfig(defaults);
      form.setFieldsValue(defaults);
    }
  }, [config, form, visualizationType, availableColumns]);

  // Smart defaults based on visualization type and available columns
  const getSmartDefaults = () => {
    if (!availableColumns || availableColumns.length === 0) {
      return DEFAULT_CONFIGS[visualizationType] || {};
    }

    const suggestions = getSmartColumnSuggestions(availableColumns, previewData, visualizationType);
    const baseConfig = DEFAULT_CONFIGS[visualizationType] || {};
    
    // For Table, build defaults using the real-time columns
    if (visualizationType === 'Table') {
      return {
        ...baseConfig,
        columns: availableColumns.map(col => ({ key: col, label: col, color: undefined }))
      };
    }

    // For KPI, default to first column if available
    if (visualizationType === 'KPI' && availableColumns.length > 0) {
      return {
        ...baseConfig,
        valueColumns: [availableColumns[0]],
        customLabels: {}
      };
    }

    // For Pie Chart, default to first two columns as name and value fields
    if (visualizationType === 'Pie Chart' && availableColumns.length >= 2) {
      return {
        ...baseConfig,
        nameField: availableColumns[0],
        valueField: availableColumns[1]
      };
    }

    return { ...baseConfig, ...suggestions };
  };

  const handleFormChange = (changedFields, allFields) => {
    const newConfig = { ...localConfig };
    changedFields.forEach(({ name, value }) => {
      newConfig[name[0]] = value;
      // Special handling for valueColumns to sync customLabels
      if (name[0] === 'valueColumns' && Array.isArray(value)) {
        const currentLabels = newConfig.customLabels || {};
        const newLabels = { ...currentLabels };
        value.forEach(col => {
          if (!newLabels.hasOwnProperty(col)) {
            newLabels[col] = '';
          }
        });
        Object.keys(newLabels).forEach(col => {
          if (!value.includes(col)) {
            delete newLabels[col];
          }
        });
        newConfig.customLabels = newLabels;
      }
    });
    setLocalConfig(newConfig);
  };

  const handleSave = () => {
    // Ensure we capture any fields not directly registered in Form (like columns edits)
    const values = form.getFieldsValue(true);
    const finalConfig = { ...getSmartDefaults(), ...values, ...localConfig };
    onConfigChange(finalConfig);
    onClose();
  };

  const handleReset = () => {
    const defaults = getSmartDefaults();
    form.setFieldsValue(defaults);
    setLocalConfig(defaults);
  };

  const renderLineChartConfig = () => (
    <div className="space-y-6">
      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            General
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 gap-4">
          <Form.Item name="visualizationName" label="Visualization Name">
            <Input placeholder="Enter visualization name" />
          </Form.Item>
          <Form.Item name="visualizationDescription" label="Visualization Description">
            <TextArea 
              placeholder="Enter visualization description (optional)" 
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </div>
      </Card>
      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            Axis Configuration
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="xAxis" label="X-Axis Column" rules={[{ required: true }]}>
            <Select placeholder="Select X-axis column">
              {availableColumns?.map(col => (
                <Option key={col} value={col}>{col}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="yAxis" label="Y-Axis Column" rules={[{ required: true }]}>
            <Select placeholder="Select Y-axis column">
              {availableColumns?.map(col => (
                <Option key={col} value={col}>{col}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Form.Item name="xAxisLabel" label="X-Axis Label">
            <Input placeholder="Enter X-axis label (e.g., User ID, Time, Category)" />
          </Form.Item>
          <Form.Item name="yAxisLabel" label="Y-Axis Label">
            <Input placeholder="Enter Y-axis label (e.g., Duration, Count, Value)" />
          </Form.Item>
        </div>
      </Card>

      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            Line Styling
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="lineType" label="Line Type">
            <Select defaultValue="monotone">
              <Option value="monotone">Smooth</Option>
              <Option value="linear">Linear</Option>
              <Option value="step">Step</Option>
              <Option value="stepBefore">Step Before</Option>
              <Option value="stepAfter">Step After</Option>
            </Select>
          </Form.Item>
          <Form.Item name="strokeWidth" label="Line Width">
            <Slider 
              min={1} 
              max={8} 
              defaultValue={2} 
              marks={{ 1: '1px', 4: '4px', 8: '8px' }}
              styles={{
                track: {
                  backgroundColor: styles.primaryColor
                },
                handle: {
                  borderColor: styles.primaryColor
                }
              }}
            />
          </Form.Item>
        </div>
      </Card>

      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            Display Options
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="showGrid" label="Show Grid" valuePropName="checked">
            <div className="visualization-config-switch">
              <Switch 
                defaultChecked 
                styles={{
                  track: {
                    background: styles.primaryColor
                  }
                }}
              />
            </div>
          </Form.Item>
          <Form.Item name="showLegend" label="Show Legend" valuePropName="checked">
            <div className="visualization-config-switch">
              <Switch 
                defaultChecked 
                styles={{
                  track: {
                    background: styles.primaryColor
                  }
                }}
              />
            </div>
          </Form.Item>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2" style={{ 
            color: styles.primaryColor,
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500
          }}>
            Line Color
          </label>
          <ColorPicker
            value={(localConfig.colors && localConfig.colors[0]) || getSmartDefaults().colors[0] || '#1d0c46'}
            onChange={(color) => {
              const newColors = [color.toHexString()];
              setLocalConfig({ ...localConfig, colors: newColors });
              form.setFieldValue('colors', newColors);
            }}
            showText
            style={{ 
              width: '100%',
              fontFamily: 'Raleway, sans-serif'
            }}
          />
        </div>
      </Card>
    </div>
  );

  const renderBarChartConfig = () => (
    <div className="space-y-6">
      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            General
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 gap-4">
          <Form.Item name="visualizationName" label="Visualization Name">
            <Input placeholder="Enter visualization name" />
          </Form.Item>
          <Form.Item name="visualizationDescription" label="Visualization Description">
            <TextArea 
              placeholder="Enter visualization description (optional)" 
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </div>
      </Card>
      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            Axis Configuration
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="xAxis" label="X-Axis Column" rules={[{ required: true }]}>
            <Select placeholder="Select X-axis column">
              {availableColumns?.map(col => (
                <Option key={col} value={col}>{col}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="yAxis" label="Y-Axis Column" rules={[{ required: true }]}>
            <Select placeholder="Select Y-axis column">
              {availableColumns?.map(col => (
                <Option key={col} value={col}>{col}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Form.Item name="xAxisLabel" label="X-Axis Label">
            <Input placeholder="Enter X-axis label (e.g., User ID, Time, Category)" />
          </Form.Item>
          <Form.Item name="yAxisLabel" label="Y-Axis Label">
            <Input placeholder="Enter Y-axis label (e.g., Duration, Count, Value)" />
          </Form.Item>
        </div>
      </Card>

      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            Bar Styling
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="barRadius" label="Bar Corner Radius">
            <Slider 
              min={0} 
              max={20} 
              defaultValue={4} 
              marks={{ 0: '0px', 10: '10px', 20: '20px' }}
              styles={{
                track: {
                  backgroundColor: styles.primaryColor
                },
                handle: {
                  borderColor: styles.primaryColor
                }
              }}
            />
          </Form.Item>
        </div>
      </Card>

      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            Display Options
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="showGrid" label="Show Grid" valuePropName="checked">
            <div className="visualization-config-switch">
              <Switch 
                defaultChecked 
                styles={{
                  track: {
                    background: styles.primaryColor
                  }
                }}
              />
            </div>
          </Form.Item>
          <Form.Item name="showLegend" label="Show Legend" valuePropName="checked">
            <div className="visualization-config-switch">
              <Switch 
                defaultChecked 
                styles={{
                  track: {
                    background: styles.primaryColor
                  }
                }}
              />
            </div>
          </Form.Item>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2" style={{ 
            color: styles.primaryColor,
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 500
          }}>
            Bar Color
          </label>
          <ColorPicker
            value={(localConfig.colors && localConfig.colors[0]) || getSmartDefaults().colors[0] || '#1d0c46'}
            onChange={(color) => {
              const newColors = [color.toHexString()];
              setLocalConfig({ ...localConfig, colors: newColors });
              form.setFieldValue('colors', newColors);
            }}
            showText
            style={{ 
              width: '100%',
              fontFamily: 'Raleway, sans-serif'
            }}
          />
        </div>
      </Card>
    </div>
  );

  const renderPieChartConfig = () => {
    // Get current nameField to extract categories from preview data
    const currentNameField = localConfig.nameField || form.getFieldValue('nameField');
    const currentValueField = localConfig.valueField || form.getFieldValue('valueField');
    
    // Extract unique categories from preview data
    const getCategories = () => {
      if (!previewData || !Array.isArray(previewData) || !currentNameField) return [];
      
      const categories = new Set();
      previewData.forEach(row => {
        if (row[currentNameField] !== undefined && row[currentNameField] !== null) {
          categories.add(String(row[currentNameField]));
        }
      });
      return Array.from(categories).sort();
    };
    
    const categories = getCategories();
    const sliceColors = localConfig.sliceColors || {}; // Map of category name to color
    
    return (
    <div className="space-y-6">
      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            General
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 gap-4">
          <Form.Item name="visualizationName" label="Visualization Name">
            <Input placeholder="Enter visualization name" />
          </Form.Item>
          <Form.Item name="visualizationDescription" label="Visualization Description">
            <TextArea 
              placeholder="Enter visualization description (optional)" 
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </div>
      </Card>
      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            Data Configuration
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item 
              name="nameField" 
              label="Category/Label Field" 
              rules={[{ required: true, message: 'Please select a category field' }]}
              help="Select the column that contains category names (e.g., Product, Region)"
            >
              <Select 
                placeholder="Select category/label column"
                value={localConfig.nameField || form.getFieldValue('nameField')}
                onChange={(value) => {
                  form.setFieldValue('nameField', value);
                  setLocalConfig({ ...localConfig, nameField: value });
                }}
                style={{ width: '100%' }}
              >
                {availableColumns?.map(col => (
                  <Option key={col} value={col}>{col}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item 
              name="valueField" 
              label="Value Field" 
              rules={[
                { required: true, message: 'Please select a value field' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('nameField') !== value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Value field must be different from category field'));
                  },
                }),
              ]}
              help="Select the column that contains numeric values to display"
            >
              <Select 
                placeholder="Select value column"
                value={localConfig.valueField || form.getFieldValue('valueField')}
                onChange={(value) => {
                  form.setFieldValue('valueField', value);
                  setLocalConfig({ ...localConfig, valueField: value });
                }}
                style={{ width: '100%' }}
              >
                {availableColumns?.map(col => (
                  <Option key={col} value={col}>{col}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="text-xs" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif', fontStyle: 'italic' }}>
            Note: If multiple rows have the same category name, their values will be automatically summed.
          </div>
        </div>
      </Card>

      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            Pie Styling
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="innerRadius" label="Inner Radius">
            <Slider 
              min={0} 
              max={100} 
              defaultValue={0} 
              marks={{ 0: '0%', 50: '50%', 100: '100%' }}
              styles={{
                track: {
                  backgroundColor: styles.primaryColor
                },
                handle: {
                  borderColor: styles.primaryColor
                }
              }}
            />
          </Form.Item>
          <Form.Item name="outerRadius" label="Outer Radius">
            <Slider 
              min={20} 
              max={150} 
              defaultValue={80} 
              marks={{ 20: '20px', 80: '80px', 150: '150px' }}
              styles={{
                track: {
                  backgroundColor: styles.primaryColor
                },
                handle: {
                  borderColor: styles.primaryColor
                }
              }}
            />
          </Form.Item>
        </div>
      </Card>

      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            Display Options
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item name="showLabels" label="Show Labels" valuePropName="checked">
            <div className="visualization-config-switch">
              <Switch 
                defaultChecked 
                styles={{
                  track: {
                    background: styles.primaryColor
                  }
                }}
              />
            </div>
          </Form.Item>
          <Form.Item name="showPercentages" label="Show Percentages" valuePropName="checked">
            <div className="visualization-config-switch">
              <Switch 
                defaultChecked 
                styles={{
                  track: {
                    background: styles.primaryColor
                  }
                }}
              />
            </div>
          </Form.Item>
        </div>
        {categories.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-3" style={{ 
              color: styles.primaryColor,
              fontFamily: 'Raleway, sans-serif',
              fontWeight: 500
            }}>
              Slice Colors
            </label>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {categories.map((category) => {
                const defaultColors = getSmartDefaults().colors || ['#1d0c46', '#caa968', '#8b5cf6', '#10b981', '#f59e0b'];
                const defaultColor = defaultColors[categories.indexOf(category) % defaultColors.length];
                const currentColor = sliceColors[category] || defaultColor;
                
                return (
                  <div key={category} className="flex items-center gap-3">
                    <div className="flex-1">
                      <Text style={{ fontSize: '12px', fontWeight: 500, color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                        {category}
                      </Text>
                    </div>
                    <div className="w-32">
                      <ColorPicker
                        value={currentColor}
                        onChange={(color) => {
                          const newSliceColors = {
                            ...sliceColors,
                            [category]: color.toHexString()
                          };
                          setLocalConfig({ ...localConfig, sliceColors: newSliceColors });
                          form.setFieldValue('sliceColors', newSliceColors);
                        }}
                        showText
                        size="small"
                        style={{ 
                          width: '100%',
                          fontFamily: 'Raleway, sans-serif'
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
    );
  };

  const renderKPIConfig = () => {
    // Get current value columns to show label inputs - use form value as source of truth
    const formValueColumns = form.getFieldValue('valueColumns');
    const currentValueColumns = formValueColumns || localConfig.valueColumns || [];
    
    return (
      <div className="space-y-6">
        <Card 
          title={
            <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
              General
            </span>
          }
          size="small" 
          style={flatCardStyle}
        >
          <div className="grid grid-cols-1 gap-4">
            <Form.Item name="visualizationName" label="Visualization Name">
              <Input placeholder="Enter visualization name" />
            </Form.Item>
            <Form.Item name="visualizationDescription" label="Visualization Description">
              <TextArea 
                placeholder="Enter visualization description (optional)" 
                rows={3}
                maxLength={500}
                showCount
              />
            </Form.Item>
          </div>
        </Card>
        
        <Card 
          title={
            <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
              KPI Configuration
            </span>
          }
          size="small" 
          style={flatCardStyle}
        >
          <div className="space-y-4">
            <Form.Item 
              name="valueColumns" 
              label="Select Metrics" 
              rules={[{ required: true, message: 'Please select at least one metric' }]}
              help="Select one or more columns to display as KPI cards"
            >
              <Select 
                mode="multiple" 
                placeholder="Select columns to display as KPIs"
                value={currentValueColumns}
                onChange={(selectedColumns) => {
                  // Update form value first
                  form.setFieldValue('valueColumns', selectedColumns);
                  
                  // Initialize custom labels for new columns
                  const currentLabels = localConfig.customLabels || {};
                  const newLabels = { ...currentLabels };
                  
                  selectedColumns.forEach(col => {
                    if (!newLabels.hasOwnProperty(col)) {
                      newLabels[col] = ''; // Empty string means use column name
                    }
                  });
                  
                  // Remove labels for columns that are no longer selected
                  Object.keys(newLabels).forEach(col => {
                    if (!selectedColumns.includes(col)) {
                      delete newLabels[col];
                    }
                  });
                  
                  // Update local config
                  setLocalConfig({ 
                    ...localConfig, 
                    valueColumns: selectedColumns, 
                    customLabels: newLabels 
                  });
                  
                  // Also update form's customLabels
                  form.setFieldValue('customLabels', newLabels);
                }}
                style={{ width: '100%' }}
              >
                {availableColumns?.map(col => (
                  <Option key={col} value={col}>{col}</Option>
                ))}
              </Select>
            </Form.Item>

            {/* Custom Labels Section - Show immediately when columns are selected */}
            {currentValueColumns.length > 0 && (
              <div className="space-y-3">
                <label className="block text-sm font-medium" style={{ 
                  color: styles.primaryColor,
                  fontFamily: 'Raleway, sans-serif',
                  fontWeight: 500,
                  marginBottom: '8px'
                }}>
                  Custom Labels (Optional)
                </label>
                {currentValueColumns.map((col) => {
                  const currentLabel = (localConfig.customLabels && localConfig.customLabels[col]) || 
                                      (form.getFieldValue('customLabels') && form.getFieldValue('customLabels')[col]) || '';
                  return (
                    <div key={col} className="space-y-1">
                      <Text style={{ fontSize: '12px', fontWeight: 500, color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                        {col}
                      </Text>
                      <Input
                        placeholder={`Label for "${col}" (leave empty to use column name)`}
                        value={currentLabel}
                        onChange={(e) => {
                          const formLabels = form.getFieldValue('customLabels') || {};
                          const newLabels = {
                            ...formLabels,
                            [col]: e.target.value
                          };
                          setLocalConfig({ ...localConfig, customLabels: newLabels });
                          form.setFieldValue('customLabels', newLabels);
                        }}
                        size="small"
                        style={{ width: '100%' }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        <Card 
          title={
            <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
              Display Options
            </span>
          }
          size="small" 
          style={flatCardStyle}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item name="layout" label="Layout">
              <Radio.Group value={localConfig.layout || form.getFieldValue('layout') || 'horizontal'}>
                <Radio value="horizontal">Horizontal</Radio>
                <Radio value="vertical">Vertical</Radio>
                <Radio value="grid">Grid</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="showTrend" label="Show Trend Indicators" valuePropName="checked">
              <div className="visualization-config-switch">
                <Switch 
                  styles={{
                    track: {
                      background: styles.primaryColor
                    }
                  }}
                />
              </div>
            </Form.Item>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2" style={{ 
              color: styles.primaryColor,
              fontFamily: 'Raleway, sans-serif',
              fontWeight: 500
            }}>
              Border Color
            </label>
            <ColorPicker
              value={(localConfig.colors && localConfig.colors[0]) || getSmartDefaults().colors[0] || '#1d0c46'}
              onChange={(color) => {
                const newColors = [color.toHexString()];
                setLocalConfig({ ...localConfig, colors: newColors });
                form.setFieldValue('colors', newColors);
              }}
              showText
              style={{ 
                width: '100%',
                fontFamily: 'Raleway, sans-serif'
              }}
            />
          </div>
        </Card>
      </div>
    );
  };

  const renderTableConfig = () => {
    const currentColumns = availableColumns || [];
    // Initialize columns configuration with actual column names from query results
    const configuredColumns = localConfig.columns && localConfig.columns.length
      ? localConfig.columns
      : currentColumns.map(col => ({ key: col, label: col, color: undefined, visible: true }));

    return (
      <div className="space-y-6">
      <Card 
        title={
          <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
            General
          </span>
        }
        size="small" 
        style={flatCardStyle}
      >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item name="tableName" label="Table Name">
              <Input placeholder="Enter table display name" />
            </Form.Item>
            <Form.Item name="pageSize" label="Rows per page">
              <InputNumber min={5} max={200} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <Form.Item name="tableDescription" label="Table Description">
              <TextArea 
                placeholder="Enter table description (optional)" 
                rows={3}
                maxLength={500}
                showCount
              />
            </Form.Item>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Form.Item name="headerColor" label="Header Background">
              <ColorPicker
                value={localConfig.headerColor}
                onChange={(c)=>{
                  const v=c.toHexString();
                  setLocalConfig({ ...localConfig, headerColor: v });
                  form.setFieldValue('headerColor', v);
                }}
                showText
                style={{ 
                  width: '100%',
                  fontFamily: 'Raleway, sans-serif'
                }}
              />
            </Form.Item>
            <Form.Item name="headerTextColor" label="Header Text Color">
              <ColorPicker
                value={localConfig.headerTextColor}
                onChange={(c)=>{
                  const v=c.toHexString();
                  setLocalConfig({ ...localConfig, headerTextColor: v });
                  form.setFieldValue('headerTextColor', v);
                }}
                showText
                style={{ 
                  width: '100%',
                  fontFamily: 'Raleway, sans-serif'
                }}
              />
            </Form.Item>
          </div>
          <Form.Item name="zebraStripes" label="Zebra Stripes" valuePropName="checked">
            <div className="visualization-config-switch">
              <Switch 
                styles={{
                  track: {
                    background: styles.primaryColor
                  }
                }}
              />
            </div>
          </Form.Item>
        </Card>

        <Card 
          title={
            <span style={{ color: styles.primaryColor, fontFamily: 'Raleway, sans-serif', fontWeight: 600 }}>
              Columns
            </span>
          }
          size="small" 
          style={flatCardStyle}
        >
          <div className="space-y-3">
            {configuredColumns.map((col, idx) => (
              <div key={col.key} className="grid grid-cols-12 items-center gap-2">
                <div className="col-span-4 text-sm">{col.key}</div>
                <div className="col-span-4">
                  <Input
                    placeholder="Display name"
                    value={col.label}
                    onChange={(e)=>{
                      const next=[...configuredColumns];
                      next[idx]={...col,label:e.target.value};
                      setLocalConfig({ ...localConfig, columns: next });
                      form.setFieldValue('columns', next);
                    }}
                    size="small"
                  />
                </div>
                <div className="col-span-4">
                  <ColorPicker
                    value={col.color}
                    onChange={(c)=>{
                      const next=[...configuredColumns];
                      next[idx]={...col,color:c.toHexString()};
                      setLocalConfig({ ...localConfig, columns: next });
                      form.setFieldValue('columns', next);
                    }}
                    showText
                    style={{ 
                      width: '100%',
                      fontFamily: 'Raleway, sans-serif'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  const getVisualizationIcon = () => {
    switch (visualizationType) {
      case 'Line Chart': return <LineChart size={16} />;
      case 'Bar Chart': return <BarChart3 size={16} />;
      case 'Pie Chart': return <PieChart size={16} />;
      case 'KPI': return <TrendingUp size={16} />;
      default: return <Settings size={16} />;
    }
  };

  const renderConfigContent = () => {
    switch (visualizationType) {
      case 'Line Chart': return renderLineChartConfig();
      case 'Bar Chart': return renderBarChartConfig();
      case 'Pie Chart': return renderPieChartConfig();
      case 'KPI': return renderKPIConfig();
      case 'Table': return renderTableConfig();
      default: return <div>Configuration not available for this visualization type.</div>;
    }
  };

  return (
    <>
      <style>{`
        .visualization-config-switch .ant-switch-checked {
          background-color: ${styles.primaryColor} !important;
        }
        .visualization-config-switch .ant-switch-checked:hover:not(.ant-switch-disabled) {
          background-color: ${styles.primaryColor} !important;
        }
      `}</style>
      <Modal
        title={
          <span style={{ 
            color: styles.primaryColor,
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 600
          }}>
            Configure {visualizationType}
          </span>
        }
        open={visible}
        onCancel={onClose}
        width={800}
      footer={
        <div className="flex justify-between">
          <Button 
            onClick={handleReset}
            className="transition-all duration-200 hover:opacity-90"
            style={{ 
              color: '#0f172a',
              borderColor: '#E5E7EB',
              borderWidth: '1px',
              borderStyle: 'solid',
              backgroundColor: '#ffffff',
              fontFamily: 'Raleway, sans-serif',
              fontWeight: 500,
              fontSize: '13px',
              borderRadius: '0px',
              padding: '6px 12px',
              height: '28px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}
          >
            Reset to Defaults
          </Button>
          <Space>
            <Button 
              onClick={onClose}
              className="transition-all duration-200 hover:opacity-90"
              style={{ 
                color: '#0f172a',
                borderColor: '#E5E7EB',
                borderWidth: '1px',
                borderStyle: 'solid',
                backgroundColor: '#ffffff',
                fontFamily: 'Raleway, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                borderRadius: '0px',
                padding: '6px 12px',
                height: '28px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              onClick={handleSave}
              className="transition-all duration-200 hover:opacity-90"
              style={{ 
                backgroundColor: styles.primaryColor, 
                borderColor: styles.primaryColor,
                border: 'none',
                color: '#ffffff',
                fontFamily: 'Raleway, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                borderRadius: '0px',
                padding: '6px 12px',
                height: '28px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}
            >
              Apply Configuration
            </Button>
          </Space>
        </div>
      }
      style={{
        top: 20
      }}
      styles={{
        content: {
          borderRadius: 0,
          padding: 0
        },
        header: {
          borderRadius: 0,
          borderBottom: '1px solid #E5E7EB',
          padding: '16px 24px',
          margin: 0
        },
        body: {
          padding: '20px 24px',
          borderRadius: 0,
          maxHeight: '60vh',
          overflowY: 'auto'
        },
        footer: {
          borderRadius: 0,
          borderTop: '1px solid #E5E7EB',
          padding: '12px 24px'
        }
      }}
    >
      <div>
        <Form
          form={form}
          layout="vertical"
          onFieldsChange={handleFormChange}
          initialValues={getSmartDefaults()}
        >
          {renderConfigContent()}
        </Form>
      </div>
    </Modal>
    </>
  );
};

export default VisualizationConfigModal;
